'use client';

import type { ReactNode } from 'react';
import { useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { useDevice } from '@/app/hooks';
import { cn } from '@/app/utils';

// FAQ 数据类型
export type FAQItem = {
  id: string;
  question: string;
  answer: Array<{
    title: string;
    link?: ReactNode;
    children?: ReactNode[] | string[];
  }>;
};

interface FAQProps {
  /** FAQ 问题数据 */
  items: FAQItem[];
  /** 自定义容器类名 */
  className?: string;
  /** 自定义内容区域容器类名 */
  classNameContainer?: string;
  /** 自定义标题类名 */
  classNameTitle?: string;
  /** 自定义列表区域容器类名 */
  classNameList?: string;
  /** 自定义标题区域容器类名 */
  classNameTitleContainer?: string;
  /** 标题文本 */
  title?: string | ReactNode;
  /** 描述文本 */
  description?: string | ReactNode;
  /** 是否显示标题（默认显示） */
  showTitle?: boolean;
  /** 自定义顶部间距 */
  customTopMargin?: string;
  /** 自定义底部间距 */
  customBottomMargin?: string;
  /** 默认展开的项 ID */
  defaultExpandedId?: string;
}

export function FAQ({
  items,
  className = '',
  classNameContainer = '',
  classNameTitle = '',
  classNameList = '',
  classNameTitleContainer = '',
  title = 'FAQ',
  description = '',
  showTitle = true,
  customTopMargin = 'mt-[200px]',
  customBottomMargin = 'mb-[100px]',
  defaultExpandedId = '',
}: FAQProps) {
  const [activeIndex, setActiveIndex] = useState<string>(defaultExpandedId);
  const { isMobile } = useDevice();

  const handleClick = (index: string) => {
    setActiveIndex(activeIndex === index ? '' : index);
  };

  return (
    <div className={`relative flex w-full flex-col justify-center overflow-hidden ${className}`}>
      <div
        className={`relative ${customTopMargin} ${customBottomMargin} flex w-full justify-center gap-[106px] md:mt-[100px] md:mb-[110px] md:flex-col md:gap-0 lg:${customTopMargin} lg:mb-[80px] xl:mt-[204px] xl:mb-[135px] ${classNameContainer}`}
      >
        {showTitle && (
          <div className={cn('ml-12 flex flex-col md:ml-0 md:items-center', classNameTitleContainer)}>
            <h2
              className={cn(
                'w-[325px] font-medium text-[54px] leading-[1.4] md:text-center md:text-[36px] lg:text-[48px]',
                classNameTitle,
              )}
            >
              {isMobile ? 'FAQ' : title}
            </h2>
            {description && (
              <div className="mt-6 w-[396px] text-[14px] text-system-text03 leading-7 md:mt-6 md:mb-[22px] md:w-[259px] md:text-center md:leading-[22px] lg:w-[320px]">
                {description}
              </div>
            )}
          </div>
        )}
        <div className={cn('mr-12 flex flex-col gap-3 md:mr-0 md:items-center', classNameList)}>
          {items.map((item) => (
            <div
              key={item.id}
              className="flex flex-col gap-3 rounded-16 bg-system-white08 px-6 py-[10px] sm:w-[325px] md:w-[600px] md:px-[17px] md:py-[14px]"
              onClick={() => handleClick(item.id)}
            >
              <div className="mt-[10px] flex items-center justify-between gap-4 font-semibold leading-7">
                <div className="font-medium text-[18px] md:w-[260px] md:text-[14px]">{item.question}</div>
                {activeIndex === item.id ? (
                  <BaseLineClose className="size-4 md:size-[12px]" />
                ) : (
                  <BaseLinePlus className="size-5 md:size-[14px]" onClick={() => handleClick(item.id)} />
                )}
              </div>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  activeIndex === item.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="w-[592px] text-[14px] leading-5 opacity-60 md:w-[291px] md:text-[12px] md:leading-[14px] lg:w-[452px] xl:w-[832px]">
                  {item.answer.map((answer) => (
                    <div key={typeof answer.title === 'string' ? answer.title : 'faq-answer'}>
                      <div className="py-3 text-[14px] leading-5 opacity-60 md:text-[12px] md:leading-[14px]">
                        {answer.title}
                        {answer.link}
                      </div>
                      {answer.children && (
                        <div className="text-[14px] leading-5 opacity-60 md:text-[12px] md:leading-[14px]">
                          <ul className="list-disc py-2 pl-4">
                            {answer.children.map((child, index) => (
                              <li key={`${index}-${typeof child === 'string' ? child : 'faq-child'}`} className="py-1">
                                {child}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
